import React, {
  createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState,
} from 'react';
import { getListingLocationSlug } from 'src/common/helpers';
import useSavedInquiry from 'src/common/hooks/useSavedInquiry';
import submitInquiry from 'src/inquiry/utils/submitInquiry';
import { inquirySubmitTracker } from 'src/inquiry/utils/sendInquiryForGuestUser';
import { getInquirySuccessUrl } from 'src/listing/utils/getBookingUrls';
import { useRouter } from 'next/router';
import { InquiryData } from 'src/inquiry/types';
import { useUserContext } from 'src/common/providers/UserProvider';
import broadcastNotification from 'src/common/components/Notification/broadcastNotification';
import { trackEvent } from 'src/common/3rdParty/tracking';
import { TripCartFormState } from 'src/listing/types';
import { Form } from 'react-final-form';
import { useTripCartContext } from 'src/trip-cart/hooks/useTripCartContext';
import TripCartForm from 'src/trip-cart/components/TripCartForm';
import { v4 as uuidv4 } from 'uuid';
import { sessionStore } from 'src/common/utils/storageWrapper';
import { EXTERNAL_PATHS, PATHS } from 'src/common/constants';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import checkForContactInfo from 'src/common/utils/checkForContactInfo';
import captureException from 'src/common/utils/captureException';
import { getDurationHours } from 'src/listing/helper';
import { formatDate } from 'src/common/utils/dateHelpers';
import { FORM_ERROR, FormApi, SubmissionErrors } from 'final-form';
import { compress } from 'lz-string';
import ensureString from 'src/common/utils/ensureString';
import getBlockedTimesForDate from 'src/trip-cart/utils/getBlockedTimesForDate';
import { validateDepartureTime } from 'src/trip-cart/hooks/useTripCartOptionsContext/getTripCartOptions';
import { clearCaches } from 'src/trip-cart/utils/tripCartCache';
import { useTranslation } from 'next-i18next';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import calculatePrices, { PriceParams } from 'src/trip-cart/utils/calculatePrices';
import { TripCartPrice } from 'src/common/types';
import bookInstantly from 'src/inquiry/utils/bookInstantly';
import { trackTripCartBookNowProceedToPaymentClick } from 'src/trip-cart/utils/trackingTripCart';
import { COMMENT_TEXT_LIMIT } from 'src/inquiry/constants';
import useTripCartPersistence from './useTripCartPersistence';
import { mapFormStateToInquiryData } from '../helper';
import TripCartSeasonsProvider from './useTripCartSeasonsContext';

type TripCartBookingContextProps = {
  isSubmitting: boolean;
  contentWarningVisible: boolean;
  setTierPricing: (values: TierPricingHistory | undefined) => void;
};

const TripCartBookingContext = createContext<TripCartBookingContextProps | null>(null);

type TierPricingHistory = {
  params: PriceParams;
  prices: TripCartPrice;
};
/**
 * Form submission for Inquiries and Instabooks (Book Now)
 */
const TripCartBookingProvider: FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const {
    listing, initialValues, showAiSummary,
    hasDepartureTimes,
  } = useTripCartContext();
  const { saveFormToStorage } = useTripCartPersistence();
  const { user } = useUserContext();
  const isUserLoggedIn = !!user?.id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const listingLocation = getListingLocationSlug(listing.breadcrumbs);
  const [contentWarningVisible, setContentWarningVisible] = useState(false);
  const { loadBookingMotivators } = useBookingMotivatorsContext();
  const [t] = useTranslation('listing-page');
  const [tierPricing, setTierPricing] = useState<TierPricingHistory>();

  const showFormError = useCallback((key: string = 'trip-cart--form-error--general') => {
    broadcastNotification({
      i18nKey: 'trip-cart--form-error--general',
      translation: 'listing-page',
      variant: 'error',
    });

    return {
      [FORM_ERROR]: key,
    };
  }, []);

  // -------
  // Inquiry
  // -------

  const {
    storeInquiryInCookies,
    storeInquiryInSessionStorage,
    inquirySearchReferer,
  } = useSavedInquiry();

  const proceedWithInquiry = useCallback(async (inquiryData: InquiryData) => {
    setIsSubmitting(true);
    const inquiryResponse = await submitInquiry(inquiryData, listing.id);
    if (inquiryResponse?.id) {
      // handle inquiry submission success
      storeInquiryInSessionStorage(inquiryData);
      inquirySearchReferer.set();
      storeInquiryInCookies(inquiryData);
      inquirySubmitTracker(
        inquiryData,
        inquiryResponse.thread_id,
        listingLocation,
        showAiSummary,
      );

      // redirect to inquiry success page
      await router.push(getInquirySuccessUrl(listing.id, inquiryResponse.id.toString()));
      return true;
    }

    // handle inquiry submission failure
    setIsSubmitting(false);
    trackEvent('inquiry_submit_fail', {
      p1: 'server-error',
      p2: showAiSummary ? 'ai_summary_visible' : 'ai_summary_hidden',
      ...(listingLocation ? { listing_location: listingLocation } : {}),
    });
    return showFormError();
  }, [
    listing.id,
    listingLocation,
    router,
    storeInquiryInCookies,
    storeInquiryInSessionStorage,
    inquirySearchReferer,
    showFormError,
    showAiSummary,
  ]);

  const isAuthMigrationEnabled = useFeatureFlag('auth-migration');

  const saveSession = useCallback((inquiryData: InquiryData): string => {
    const key = uuidv4();

    // compress the comment field to reduce the size of the data
    const data = {
      ...inquiryData,
      comment: compress(ensureString(inquiryData.comment)),
    };
    sessionStore.setItem(key, JSON.stringify(data));
    return key;
  }, []);

  const proceedWithGuestUserInquiry = useCallback((inquiryData: InquiryData) => {
    try {
      /**
       * generate a unique key for the session storage and
       * store the inquiry data in it
       */
      setIsSubmitting(true);
      const key = saveSession(inquiryData);

      inquirySearchReferer.set();

      const params = new URLSearchParams({
        next: `${PATHS.LISTING}${listing.id}${PATHS.INQUIRY_SUBMIT}?sessionKey=${key}`,
        return: `${PATHS.LISTING}${listing.id}/?sessionKey=${key}&sheet`,
      });

      if (isAuthMigrationEnabled) {
        const registerUrl = `${PATHS.REGISTER}?${params.toString()}`;
        window.location.href = registerUrl;
      } else {
        const contactUrl = `${EXTERNAL_PATHS.INQUIRY}${listing.id}/contact/?${params.toString()}`;
        window.location.href = contactUrl;
      }

      return true;
    } catch (error) {
      setIsSubmitting(false);
      captureException(`Failed to read sessionStorage: ${error}`);
      return showFormError();
    }
  }, [saveSession, inquirySearchReferer, listing.id, isAuthMigrationEnabled, showFormError]);

  const initiateInquiry = useCallback((values: TripCartFormState) => {
    if (!values.departureTime) {
      // Errors handled in submit
      return false;
    }
    const inquiryData = mapFormStateToInquiryData(values);
    if (isUserLoggedIn) {
      // submit inquiry for logged in user.
      return proceedWithInquiry(inquiryData);
    }
    // handle logged out user inquiry
    return proceedWithGuestUserInquiry(inquiryData);
  }, [isUserLoggedIn, proceedWithGuestUserInquiry, proceedWithInquiry]);

  const proceedWithGuestUserInstabook = useCallback((inquiry: InquiryData) => {
    if (!inquiry.pickup_time) {
      return false;
    }

    const key = saveSession(inquiry);
    const returnUrl = `${PATHS.LISTING}${listing.id}/?sessionKey=${key}`;
    const nextUrlParams = new URLSearchParams({
      duration: inquiry.trip_length,
      guests: `${inquiry.adults}`,
      captain: `${inquiry.captain_required}`,
      date: inquiry.preferred_date1,
      departure: inquiry.pickup_time,
      return: returnUrl,
    });
    const comment = ensureString(inquiry.comment?.slice(0, COMMENT_TEXT_LIMIT));
    // Add comment at end so we don't lose critical content
    if (comment?.length) {
      // Ensure we don't overflow text limits
      const compressed = compress(comment);
      nextUrlParams.append('comment', compressed);
    }
    const nextUrl = `${PATHS.LISTING}${listing.id}/checkout/guest?${nextUrlParams.toString()}`;
    const params = new URLSearchParams({
      next: nextUrl,
      return: returnUrl,
    });

    const url = isAuthMigrationEnabled
      ? `${PATHS.REGISTER}?${params.toString()}`
      : `${EXTERNAL_PATHS.REGISTER}?${params.toString()}`;
    window.location.href = url;
    return true;
  }, [isAuthMigrationEnabled, listing.id, saveSession]);

  const initiateInstabook = useCallback(async (values: TripCartFormState) => {
    if (!values.departureTime) {
      // Errors handled in submit
      return false;
    }
    setIsSubmitting(true);
    trackTripCartBookNowProceedToPaymentClick();

    const inquiryData = mapFormStateToInquiryData(values);
    if (!isUserLoggedIn) {
      return proceedWithGuestUserInstabook(inquiryData);
    }

    const instabookTrip = await bookInstantly(inquiryData, listing.id);
    if (!instabookTrip) {
      setIsSubmitting(false);
      return showFormError();
    }

    const key = saveSession(inquiryData);
    const searchParams = new URLSearchParams({
      return: `${PATHS.LISTING}${listing.id}/?sessionKey=${key}`,
    }).toString();
    const url = `${PATHS.LISTING}${listing.id}/checkout/${instabookTrip.pk}/?${searchParams}`;

    router.push(url);
    return true;
  }, [
    isUserLoggedIn,
    listing.id,
    proceedWithGuestUserInstabook,
    router,
    saveSession,
    showFormError,
  ]);

  // -------
  // General
  // -------

  const validateBlockedTimesForSubmit = useCallback(async (
    values: TripCartFormState,
  ): Promise<SubmissionErrors | undefined> => {
    // ignore cache
    const blockedTimes = await getBlockedTimesForDate(listing.id, values.formSelectedDates[0]);
    const error = validateDepartureTime({
      value: values.departureTime!,
      durationHours: getDurationHours(values.formDuration),
      blockedTimes,
      date: values.formSelectedDates[0],
      listingTimeZone: listing.timezone,
      t,
    });
    if (error === undefined) {
      return undefined;
    }

    return showFormError('trip-cart--form-error--submit-blocked');
  }, [listing.id, listing.timezone, showFormError, t]);

  const checkPricingMatches = useCallback(async (): Promise<boolean> => {
    if (!tierPricing?.params || !tierPricing?.prices) {
      return true;
    }

    const priceData = await calculatePrices(listing.id, { ...tierPricing.params });
    if (!priceData || !priceData.price_sections) {
      // Don't block the booking
      return true;
    }

    // Server error, duration/date is not available or doesn't exist
    if (!priceData.price_sections.length) {
      return false;
    }

    return (priceData.renter_booking_amount === tierPricing.prices.renter_booking_amount);
  }, [listing.id, tierPricing]);

  const submit = useCallback(async (
    values: TripCartFormState,
    form: FormApi<TripCartFormState, Partial<TripCartFormState>>,
  ): Promise<SubmissionErrors | boolean> => {
    if (!values.departureTime) {
      // track validation error for inquiry
      if (values.bookingMode === 'inquiry') {
        trackEvent('inquiry_submit_fail', {
          p1: 'validation-error',
          ...(listingLocation ? { listing_location: listingLocation } : {}),
        });
      }

      return {
        departureTime: !hasDepartureTimes ? 'trip-cart--form-error--no-departure-times' : 'trip-cart--form-error--departure-time',
      };
    }
    const { errors } = form.getState();
    if (Object.keys(errors ?? {}).length) {
      // Shown per field
      return false;
    }

    if (!contentWarningVisible && values.message) {
      try {
        const hasContactInfo = await checkForContactInfo([values.message]);
        if (hasContactInfo) {
          // 'content' errors will get auto-fixed by the backend, content window will run submit
          setContentWarningVisible(true);
          return true;
        }
      } catch (error) {
        captureException(error);
        return showFormError();
      }
    }

    // In case user reloads after error
    saveFormToStorage(values);

    const isPricingMatched = await checkPricingMatches();
    if (!isPricingMatched) {
      return showFormError('trip-cart--form-error--submit-price-mismatch');
    }

    const blockedError = await validateBlockedTimesForSubmit(values);
    if (blockedError) {
      // cache is no longer valid, clear all
      clearCaches();
      loadBookingMotivators(formatDate(values.formSelectedDates[0]), true);
      return blockedError;
    }

    if (values.bookingMode === 'instabook') {
      return initiateInstabook(values);
    }
    return initiateInquiry(values);
  }, [
    validateBlockedTimesForSubmit,
    loadBookingMotivators,
    checkPricingMatches,
    contentWarningVisible,
    saveFormToStorage,
    showFormError,
    listingLocation,
    initiateInquiry,
    initiateInstabook,
    hasDepartureTimes,
  ]);

  const value = useMemo((): TripCartBookingContextProps => ({
    isSubmitting,
    contentWarningVisible,
    setTierPricing,
  }), [isSubmitting, contentWarningVisible, setTierPricing]);

  return (
    <TripCartBookingContext.Provider value={value}>
      <Form<TripCartFormState>
        keepDirtyOnReinitialize
        initialValues={initialValues}
        validateOnBlur
        onSubmit={submit}
      >
        {() => (
          <TripCartForm>
            <TripCartSeasonsProvider listing={listing}>
              {children}
            </TripCartSeasonsProvider>
          </TripCartForm>
        )}
      </Form>
    </TripCartBookingContext.Provider>
  );
};

export default TripCartBookingProvider;

export const useTripCartBookingContext = (): TripCartBookingContextProps => {
  const context = useContext(TripCartBookingContext);
  if (context == null) {
    throw new Error('useTripCartBookingContext must be used within a TripCartBookingProvider');
  }

  return context;
};
